import React, { useCallback } from 'react'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ParagraphMD, TitleLG } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { getStringFromLocalStorage, removeFromLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { createZendeskLink, trackUserContactSupport } from '@/views/TicketCheckoutViews/utils/errorTrackingUtils'

interface ContactSupportModalProps {
  open: boolean
  onClose: () => void
  reservationId: string
}
export const ContactSupportReservationModal = ({ open, onClose, reservationId }: ContactSupportModalProps) => {
  const { t } = useTranslate('tickets')
  const { reload, push } = useSafeRouter()
  const { user } = useUser()
  const track = useSafeTrack()

  const handleRetry = useCallback(() => {
    reload()
    onClose()
  }, [reload, onClose])

  const onContactSupportClick = useCallback(() => {
    const errorId = getStringFromLocalStorage('ATOM_ERROR_ID')
    trackUserContactSupport({ context: 'Theatrical User Contacted Support', uuid: errorId ?? 'UNKNOWN', track })
    removeFromLocalStorage('ATOM_ERROR_ID')
    push(createZendeskLink({ reservationId, errorId, email: user?.email ?? '' }))
  }, [push, reservationId, track, user?.email])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="!bg-core-gray-950 lg:min-w-[500px] border-2 border-solid border-core-gray-900 !p-10 relative"
      transition="appear"
      className="z-50 text-white"
    >
      <div>
        <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-core-gray-900 p-1" onClick={onClose}>
          <CloseIcon color="white" size={18} />
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 px-2">
            <TitleLG weight="bold">{t('sorry', 'Sorry!')}</TitleLG>
            <ParagraphMD>
              {t(
                'contactSupportMessage',
                'An unexpected error has occurred on our end and we’re working to fix the issue. Please retry again later. If the problem persists, please contact customer support. ',
              )}
            </ParagraphMD>
          </div>
          <div className="flex flex-col gap-1">
            <Button
              variant="white"
              onClick={handleRetry}
              className="mx-auto mb-2 flex min-w-[300px] stroke-white py-3 hover:stroke-core-gray-950"
            >
              <div>{t('retry', 'Retry')}</div>
            </Button>
            <Button
              className="mx-auto flex min-w-[300px] bg-core-gray-900 stroke-white py-3 hover:stroke-core-gray-950"
              onClick={onContactSupportClick}
              variant="core-gray-800"
            >
              {t('contactCustomerSupport', 'Contact Customer Support')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
